/**
 * useClassAllocation hook
 * Fetches kanban board data for one course and provides move/auto-assign mutations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';

export function useClassAllocationSummary() {
  return useQuery({
    queryKey: ['class-allocation', 'summary'],
    queryFn: () => api.classAllocation.getSummary(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useMyClasses() {
  return useQuery({
    queryKey: ['class-allocation', 'my-classes'],
    queryFn: () => api.classAllocation.getMyClasses(),
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
}

export function useClassAllocation(courseUuid) {
  const queryClient = useQueryClient();
  const queryKey = ['class-allocation', courseUuid];

  const query = useQuery({
    queryKey,
    queryFn: () => api.classAllocation.getData(courseUuid),
    enabled: !!courseUuid,
    staleTime: 30 * 1000,
  });

  // ── Move mutation (with optimistic update) ──────────────────────────────
  const moveMutation = useMutation({
    mutationFn: ({ studentUuids, classGroupUuid }) =>
      api.classAllocation.move(studentUuids, classGroupUuid),

    onMutate: async ({ studentUuids, classGroupUuid }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData(queryKey);

      queryClient.setQueryData(queryKey, (old) => {
        if (!old) return old;

        // Update students' class_group_uuid
        const updatedStudents = old.students.map((s) =>
          studentUuids.includes(s.uuid) ? { ...s, class_group_uuid: classGroupUuid } : s
        );

        // Update class student lists + counts
        const updatedClasses = old.classes.map((cg) => {
          let students = cg.students.filter((u) => !studentUuids.includes(u));
          if (classGroupUuid && cg.uuid === classGroupUuid) {
            students = [...students, ...studentUuids];
          }
          return {
            ...cg,
            students,
            current_enrollment: students.length,
            available_spots: Math.max(0, cg.capacity - students.length),
          };
        });

        return { ...old, students: updatedStudents, classes: updatedClasses };
      });

      return { previous };
    },

    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['class-allocation', 'summary'] });
    },
  });

  // ── Auto-assign mutation (no optimistic update — too many changes) ───────
  const autoAssignMutation = useMutation({
    mutationFn: ({ courseUuid: cUuid, reassignAll }) =>
      api.classAllocation.autoAssign(cUuid, reassignAll),

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
      queryClient.invalidateQueries({ queryKey: ['class-allocation', 'summary'] });
    },
  });

  // ── Clear mutation ───────────────────────────────────────────────────────
  const clearMutation = useMutation({
    mutationFn: ({ courseUuid: cUuid, password }) =>
      api.classAllocation.clear(cUuid, password),

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['class-allocation', 'summary'] });
    },
  });

  return {
    // Data
    course: query.data?.course ?? null,
    classes: query.data?.classes ?? [],
    students: query.data?.students ?? [],

    // State
    isLoading: query.isLoading,
    error: query.error,

    // Move action
    moveStudents: (studentUuids, classGroupUuid) =>
      moveMutation.mutateAsync({ studentUuids, classGroupUuid }),
    isMoving: moveMutation.isPending,
    moveError: moveMutation.error,

    // Auto-assign action
    autoAssign: (cUuid, reassignAll = false) =>
      autoAssignMutation.mutateAsync({ courseUuid: cUuid, reassignAll }),
    isAutoAssigning: autoAssignMutation.isPending,
    autoAssignError: autoAssignMutation.error,

    // Clear action
    clearAssignments: (cUuid, password) =>
      clearMutation.mutateAsync({ courseUuid: cUuid, password }),
    isClearing: clearMutation.isPending,
    clearError: clearMutation.error,

    // Direct refetch for the board (bypasses key-matching)
    refetchBoard: query.refetch,
  };
}

export default useClassAllocation;
